import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { adminApi } from "../../../api/resources";
import type { EmployeePublic } from "../../../api/types";
import { SelectFilter } from "../../../components/Overlay";
import { DemoBadge, StatusPill, TextInput } from "../../../components/ui";
import { EMPLOYEE_STATUS_LABEL } from "../../../utils/labels";
import { SmsStatusPill, lastActivity } from "../sms/smsUi";
import { DataTable, LoadState, useLoad } from "../shared";

const STATUS_OPTIONS: [string, string][] = [["", "All statuses"], ...Object.entries(EMPLOYEE_STATUS_LABEL)];

export function EmployeesTab({ status, onStatus }: { status: string; onStatus: (s: string) => void }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  const loader = useCallback(() => adminApi.employees(debounced || undefined, status || undefined), [debounced, status]);
  const { data, error } = useLoad<EmployeePublic[]>(loader);

  return (
    <div data-testid="employees-tab">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-zinc-500" />
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone"
            className="pl-9" data-testid="employees-search-input" />
        </div>
        <SelectFilter value={status} onChange={onStatus} options={STATUS_OPTIONS} testId="employees-status-filter" label="Status filter" />
      </div>
      <LoadState error={error} loading={!data} testId="employees-load-error" />
      {data && (
        <DataTable testId="employees-table" headers={["Name", "Email", "Phone", "Status", "SMS", "Last SMS"]} empty={data.length === 0}>
          {data.map((e) => (
            <tr key={e.id} data-testid={`employee-row-${e.id}`} className="transition-colors hover:bg-white/[0.03]">
              <td className="px-5 py-4">
                <Link to={`/admin/employees/${e.id}`} data-testid={`employee-link-${e.id}`} className="mr-2 text-white hover:text-signal">{e.full_name}</Link>
                <DemoBadge show={e.is_demo} testId={`employee-demo-badge-${e.id}`} />
              </td>
              <td className="px-5 py-4 text-zinc-300">{e.email}</td>
              <td className="px-5 py-4 text-zinc-300">
                <span className="font-mono text-xs">{e.phone_e164 ?? "—"}</span>
                <span className={`ml-2 text-xs ${e.phone_verified ? "text-emerald-400" : "text-zinc-500"}`}>{e.phone_verified ? "verified" : "unverified"}</span>
              </td>
              <td className="px-5 py-4"><StatusPill status={e.account_status} label={EMPLOYEE_STATUS_LABEL[e.account_status]} testId={`employee-status-${e.id}`} /></td>
              <td className="px-5 py-4">
                {e.sms && <SmsStatusPill status={e.sms.sms_status} testId={`employee-sms-status-${e.id}`} />}
                {!!e.sms?.failed_verification_attempts && <p data-testid={`employee-failed-attempts-${e.id}`} className="mt-1 text-[11px] text-zinc-500">{e.sms.failed_verification_attempts} failed</p>}
              </td>
              <td className="px-5 py-4 text-xs text-zinc-400" data-testid={`employee-last-sms-${e.id}`}>{lastActivity(e.sms)}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}
